/**
 * PlanScheduleTable — a payment plan's installment schedule (US 4396).
 * Desktop table + stacked mobile list. The schedule is computed server-side
 * (Bss.Entities PaymentPlanSchedule) — the same one Bankers staff see in b360.
 */
import { formatCurrencyWithSymbol } from '../../powerreporting/utils/formatters';
import {
  formatScheduleDate,
  SCHEDULE_STATUS,
  SCHEDULE_KIND,
  SCHEDULE_LINE_STATUS,
} from '../utils/scheduleFormat';

const StatusPill = ({ status }) => {
  const meta = SCHEDULE_STATUS[status] || SCHEDULE_STATUS[SCHEDULE_LINE_STATUS.Upcoming];
  return (
    <span className={`inline-block px-2 py-0.5 text-[11px] font-semibold rounded-full whitespace-nowrap ${meta.className}`}>
      {meta.label}
    </span>
  );
};

const lineLabel = (line) => (line.kind === SCHEDULE_KIND.LateFee ? 'Late fee' : `#${line.sequence}`);

const PlanScheduleTable = ({ schedule = [] }) => {
  if (schedule.length === 0) return null;

  return (
    <div>
      {/* Desktop */}
      <div className="hidden sm:block overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
              <th className="py-2 pr-4 font-medium">Installment</th>
              <th className="py-2 pr-4 font-medium">Due</th>
              <th className="py-2 pr-4 font-medium text-right">Amount</th>
              <th className="py-2 pr-4 font-medium text-right">Paid</th>
              <th className="py-2 pr-4 font-medium text-right">Still owed</th>
              <th className="py-2 font-medium">Status</th>
            </tr>
          </thead>
          <tbody>
            {schedule.map((line, index) => (
              <tr
                key={`${line.kind}-${line.sequence ?? 'fee'}-${index}`}
                className={`border-b border-gray-100 dark:border-gray-700/50 last:border-0 ${
                  line.status === SCHEDULE_LINE_STATUS.PastDue ? 'bg-rose-50/60 dark:bg-rose-900/10' : ''
                }`}
              >
                <td className="py-2 pr-4 text-gray-900 dark:text-white">{lineLabel(line)}</td>
                <td className="py-2 pr-4 text-gray-700 dark:text-gray-300 whitespace-nowrap">{formatScheduleDate(line.dueDate)}</td>
                <td className="py-2 pr-4 text-right text-gray-900 dark:text-white">{formatCurrencyWithSymbol(line.amount)}</td>
                <td className="py-2 pr-4 text-right text-gray-600 dark:text-gray-400">
                  {line.paid > 0 ? formatCurrencyWithSymbol(line.paid) : '—'}
                </td>
                <td className="py-2 pr-4 text-right font-medium text-gray-900 dark:text-white">
                  {line.outstanding > 0 ? formatCurrencyWithSymbol(line.outstanding) : '—'}
                </td>
                <td className="py-2"><StatusPill status={line.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile */}
      <ul className="sm:hidden divide-y divide-gray-100 dark:divide-gray-700">
        {schedule.map((line, index) => (
          <li key={`${line.kind}-${line.sequence ?? 'fee'}-${index}`} className="py-2 flex items-center justify-between gap-3">
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-white">
                {lineLabel(line)} · {formatScheduleDate(line.dueDate)}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {formatCurrencyWithSymbol(line.amount)}
                {line.outstanding > 0 && line.paid > 0 ? ` · ${formatCurrencyWithSymbol(line.outstanding)} still owed` : ''}
              </p>
            </div>
            <StatusPill status={line.status} />
          </li>
        ))}
      </ul>
    </div>
  );
};

export default PlanScheduleTable;
